import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Mail, Phone, MapPin, BadgeCheck, Star, ArrowRight, Instagram } from "lucide-react";
import { Link } from "react-router-dom";
import { CONTACT_EMAIL, CONTACT_PHONE_DISPLAY, CONTACT_PHONE_E164 } from "@/lib/contact";
import { buildContactPath } from "@/lib/contactPath";
import { submitLeadForm } from "@/lib/web3forms";

const Footer = () => {
  const [newsletterName, setNewsletterName] = useState("");
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [newsletterPhone, setNewsletterPhone] = useState("");
  const [newsletterAddress, setNewsletterAddress] = useState("");
  const [newsletterBedrooms, setNewsletterBedrooms] = useState("");
  const [newsletterHoneypot, setNewsletterHoneypot] = useState("");
  const [newsletterStatus, setNewsletterStatus] = useState<{
    type: "success" | "error";
    message: string;
  } | null>(null);
  const [isNewsletterSubmitting, setIsNewsletterSubmitting] = useState(false);

  const footerLinks = {
    "For Hosts": [
      { name: "Property Management", href: "/services" },
      { name: "Interior Styling", href: "/services" },
      { name: "Dynamic Pricing", href: "/services" },
      { name: "Revenue Calculator", href: "/#calculator" },
    ],
    Locations: [
      { name: "Northern Beaches", href: "/northern-beaches" },
      { name: "North Shore", href: "/north-shore" },
      { name: "Lower North Shore", href: "/lower-north-shore" },
      { name: "Eastern Suburbs", href: "/bondi" },
      { name: "Sydney CBD", href: "/sydney-cbd" },
      { name: "Inner Sydney", href: "/inner-sydney" },
    ],
    Compliance: [
      { name: "STRA Compliance", href: "/stra-compliance" },
      { name: "NSW Requirements", href: "/nsw-requirements" },
      { name: "Insurance", href: "/insurance" },
    ],
    Company: [
      { name: "About Us", href: "/about" },
      { name: "Managed Properties", href: "/usha-listings" },
      { name: "Blog", href: "/blog" },
      { name: "Contact", href: "/contact" },
    ],
  };

  const handleNewsletterSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setNewsletterStatus(null);
    setIsNewsletterSubmitting(true);

    const result = await submitLeadForm({
      formName: "Footer Contact",
      name: newsletterName,
      phone: newsletterPhone,
      email: newsletterEmail,
      message: `Footer contact request from site visitor. Email: ${newsletterEmail}. Property address: ${newsletterAddress}. Bedrooms: ${newsletterBedrooms}.`,
      metadata: {
        source: "footer-inline-capture",
        intent: "quick-call-request",
        property_address: newsletterAddress,
        bedrooms: newsletterBedrooms,
      },
      honeypot: newsletterHoneypot,
    });

    if (result.ok) {
      setNewsletterStatus({
        type: "success",
        message: "Thanks. We received your details and will contact you shortly.",
      });
      setNewsletterName("");
      setNewsletterEmail("");
      setNewsletterPhone("");
      setNewsletterAddress("");
      setNewsletterBedrooms("");
      setNewsletterHoneypot("");
    } else {
      setNewsletterStatus({
        type: "error",
        message: result.message,
      });
    }

    setIsNewsletterSubmitting(false);
  };

  return (
    <footer className="text-foreground" style={{ background: '#FFFFFF', borderTop: '1px solid #E5E5E5' }}>
      {/* Main Footer */}
      <div className="container mx-auto px-4 sm:px-6 pt-14 sm:pt-20 pb-10 sm:pb-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-12 lg:gap-20 mb-12 md:mb-16">
          {/* Left - Brand & Newsletter */}
          <div>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl mb-5 sm:mb-6">
              Parvatii<span className="text-secondary">Stays</span>
            </h2>
            <p className="text-muted-foreground text-base sm:text-lg mb-6 sm:mb-8 max-w-md">
              Sydney&apos;s Northern Beaches, North Shore, Lower North Shore, Eastern Suburbs, and Sydney CBD short-term rental specialists.
              We handle guests, cleaning, and compliance while you earn more. Real people. Real accountability.
              Properties properly looked after.
            </p>

            {/* Newsletter */}
            <div className="mb-8">
              <p className="text-sm font-medium mb-3">Request a quick call from our team</p>
              <form className="grid grid-cols-1 sm:grid-cols-2 gap-3" onSubmit={handleNewsletterSubmit}>
                <label htmlFor="footer-contact-name" className="sr-only">
                  Full name
                </label>
                <Input
                  id="footer-contact-name"
                  type="text"
                  placeholder="Enter your name"
                  value={newsletterName}
                  onChange={(event) => setNewsletterName(event.target.value)}
                  className="flex-1 rounded-full bg-muted border-border text-foreground placeholder:text-muted-foreground"
                  autoComplete="name"
                  required
                />
                <label htmlFor="footer-contact-email" className="sr-only">
                  Email address
                </label>
                <Input
                  id="footer-contact-email"
                  type="email"
                  placeholder="Email Address"
                  value={newsletterEmail}
                  onChange={(event) => setNewsletterEmail(event.target.value)}
                  className="flex-1 rounded-full bg-muted border-border text-foreground placeholder:text-muted-foreground"
                  autoComplete="email"
                  required
                />
                <label htmlFor="footer-contact-phone" className="sr-only">
                  Phone number
                </label>
                <Input
                  id="footer-contact-phone"
                  type="tel"
                  placeholder="Enter your phone"
                  value={newsletterPhone}
                  onChange={(event) => setNewsletterPhone(event.target.value)}
                  className="flex-1 rounded-full bg-muted border-border text-foreground placeholder:text-muted-foreground"
                  inputMode="tel"
                  autoComplete="tel"
                  required
                />
                <label htmlFor="footer-contact-address" className="sr-only">
                  Property address
                </label>
                <Input
                  id="footer-contact-address"
                  type="text"
                  placeholder="Property Address"
                  value={newsletterAddress}
                  onChange={(event) => setNewsletterAddress(event.target.value)}
                  className="flex-1 rounded-full bg-muted border-border text-foreground placeholder:text-muted-foreground"
                  autoComplete="street-address"
                  required
                />
                <label htmlFor="footer-contact-bedrooms" className="sr-only">
                  Number of bedrooms
                </label>
                <Input
                  id="footer-contact-bedrooms"
                  type="text"
                  placeholder="Number of Bedrooms"
                  value={newsletterBedrooms}
                  onChange={(event) => setNewsletterBedrooms(event.target.value)}
                  className="flex-1 rounded-full bg-muted border-border text-foreground placeholder:text-muted-foreground"
                  required
                />
                <label htmlFor="footer-contact-company" className="sr-only">
                  Company
                </label>
                <Input
                  id="footer-contact-company"
                  type="text"
                  tabIndex={-1}
                  autoComplete="off"
                  value={newsletterHoneypot}
                  onChange={(event) => setNewsletterHoneypot(event.target.value)}
                  className="absolute -left-[9999px] top-auto h-px w-px opacity-0 pointer-events-none"
                  aria-hidden="true"
                />
                <Button
                  type="submit"
                  disabled={isNewsletterSubmitting}
                  className="rounded-full px-6 bg-foreground hover:bg-foreground/90 text-background btn-lift w-full sm:col-span-2"
                >
                  {isNewsletterSubmitting ? "Submitting..." : "Request Call"}
                </Button>
              </form>
              {newsletterStatus ? (
                <p
                  className={`mt-3 text-xs sm:text-sm font-medium ${
                    newsletterStatus.type === "success" ? "text-emerald-600" : "text-red-600"
                  }`}
                >
                  {newsletterStatus.message}
                </p>
              ) : null}
            </div>

            {/* Social */}
            <div className="flex items-center gap-3 mb-6">
              <a
                href="https://www.instagram.com/parvatiistays"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="ParvatiiStays on Instagram"
                className="inline-flex items-center justify-center h-10 w-10 rounded-full border border-border bg-muted hover:bg-foreground hover:text-background hover:border-foreground transition-colors text-muted-foreground"
              >
                <Instagram className="h-4 w-4" />
              </a>
            </div>

            {/* Contact Info */}
            <div className="space-y-3 text-sm text-muted-foreground">
              <a href={`mailto:${CONTACT_EMAIL}`} className="flex items-start gap-3 hover:text-foreground transition-colors break-all">
                <Mail className="h-4 w-4" />
                {CONTACT_EMAIL}
              </a>
              <a href={`tel:${CONTACT_PHONE_E164}`} className="flex items-center gap-3 hover:text-foreground transition-colors">
                <Phone className="h-4 w-4" />
                {CONTACT_PHONE_DISPLAY}
              </a>
              <a
                href="https://www.google.com/maps?q=Sydney+NSW+Australia"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-start gap-3 hover:text-foreground transition-colors"
              >
                <MapPin className="h-4 w-4" />
                Northern Beaches, North Shore, Lower North Shore, Eastern Suburbs & Sydney CBD, NSW
              </a>
            </div>
          </div>

          {/* Right - Links */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {Object.entries(footerLinks).map(([category, links]) => (
              <div key={category}>
                <h3 className="font-medium text-sm mb-4 text-muted-foreground uppercase tracking-wider">
                  {category}
                </h3>
                <ul className="space-y-3">
                  {links.map((link) => (
                    <li key={link.name}>
                      <Link
                        to={link.href}
                        className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {link.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-border pt-8">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-4 lg:gap-6">
            <p className="text-sm text-muted-foreground text-center md:text-left">
              © {new Date().getFullYear()} ParvatiiStays Sydney. All rights reserved.
            </p>
            <p className="text-xs text-muted-foreground/90 text-center md:text-left">
              ABN: Available on request.
            </p>

            <div className="flex flex-col sm:flex-row items-center gap-2 sm:gap-3">
              <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted px-3 py-1.5 text-xs font-semibold text-foreground">
                <BadgeCheck className="h-3.5 w-3.5" />
                Airbnb Superhost Standards
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-muted px-3 py-1.5 text-xs font-semibold text-foreground">
                <Star className="h-3.5 w-3.5 fill-current" />
                4.9 Rating (200+ reviews)
              </span>
            </div>

            <div className="flex items-center">
              <a
                href="https://www.airbnb.co.in/co-hosts/profile/1031662611173426333"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 rounded-full border border-border bg-muted px-4 py-2 text-xs font-semibold text-foreground hover:bg-foreground hover:text-background transition-colors"
              >
                Airbnb Co-Host Profile
                <ArrowRight className="h-3.5 w-3.5" />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Final CTA */}
      <div className="border-t border-white/10 bg-[#111827] text-white">
        <div className="container mx-auto px-4 sm:px-6 py-10 sm:py-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-white/70 mb-2">Owner Guarantee</p>
            <h3 className="font-serif text-2xl sm:text-3xl leading-tight mb-2">
              Free revenue audit. For every Sydney property owner.
            </h3>
          </div>

          <Button asChild className="rounded-full bg-white text-[#111827] hover:bg-white/90 font-bold px-6 sm:px-8 min-h-[48px]">
            <Link to={buildContactPath({ source: "footer-owner-guarantee", intent: "free-revenue-audit" })}>
              Book Your Free Audit
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
